import { useRef } from "react";
import { MapPin, Phone, Mail, Clock, Siren } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import * as _frappeSDK from "frappe-react-sdk";
const _f: any = (_frappeSDK as any).default || _frappeSDK;
const { useFrappePostCall } = _f;
import { toast } from "sonner";
import { PageShell, PageHero } from "@/components/knmc/PageShell";
import { LocationMap } from "@/components/knmc/LocationMap";
import type { ContactSettings } from "@/hooks/useContactSettings";
import { parseFrappeError } from "@/lib/errors";

export default function Contact({ settings }: { settings: ContactSettings }) {
  const { call: sendContactMessage, loading: sending } = useFrappePostCall(
    "health_web.api.contact.send_contact_message",
  );

  const formRef = useRef<HTMLFormElement>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    try {
      await sendContactMessage({
        full_name: data.get("name") as string,
        email: data.get("email") as string,
        subject: data.get("subject") as string,
        message: data.get("message") as string,
        phone: data.get("phone") as string,
      });
      toast.success("Message sent successfully");
      form.reset();
    } catch (err) {
      toast.error(parseFrappeError(err));
    }
  };

  const infoCards = settings
    ? [
        {
          icon: MapPin,
          title: "Address",
          lines: settings.address.formatted.split("\n"),
        },
        {
          icon: Phone,
          title: "Phone",
          lines: settings.phone.split("\n"),
        },
        {
          icon: Mail,
          title: "Email",
          lines: [settings.email_id],
        },
        {
          icon: Clock,
          title: "OPD Hours",
          lines: settings.office_hours ? settings.office_hours.split("\n") : [],
        },
      ]
    : [];

  return (
    <PageShell>
      <PageHero
        eyebrow="Contact"
        title="We're here to help"
        description="For appointments, general queries, or feedback — reach us using any of the channels below."
      />

      <section className="py-20">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 lg:grid-cols-[1fr_1.2fr]">
          <div className="space-y-6">
            {infoCards.map(({ icon: Icon, title, lines }) => (
              <div key={title} className="flex gap-4 rounded-2xl border border-border bg-card p-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display font-space text-lg font-semibold text-foreground">{title}</h3>
                  {lines.map((l) => (
                    <p key={l} className="text-sm text-muted-foreground">{l}</p>
                  ))}
                </div>
              </div>
            ))}

            {settings.emergency_phone && (
              <div className="flex items-start gap-4 rounded-2xl border border-emergency/30 bg-emergency/5 p-6">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emergency text-white">
                  <Siren className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-display font-space text-lg font-semibold text-emergency">24/7 Emergency</h3>
                  <p className="text-sm text-foreground/80">
                    Dial{" "}
                    <a className="font-semibold underline" href={`tel:${settings.emergency_phone}`}>
                      {settings.emergency_phone}
                    </a>{" "}
                    for ambulance & trauma response.
                  </p>
                </div>
              </div>
            )}
          </div>

          <form ref={formRef} onSubmit={onSubmit} className="rounded-3xl border border-border bg-card p-8 md:p-10">
            <h2 className="font-display text-2xl font-semibold text-primary">Send us a message</h2>
            <p className="mt-1 text-sm text-muted-foreground">We typically respond within 1 business day.</p>
            <div className="mt-6 grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" name="name" required className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" name="phone" type="tel" required className="mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" name="email" type="email" required className="mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="subject">Subject</Label>
                <Input id="subject" name="subject" required className="mt-1.5" />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="message">Message</Label>
                <Textarea id="message" name="message" rows={5} required className="mt-1.5" />
              </div>
            </div>
            <Button type="submit" disabled={sending} className="mt-6 w-full rounded-full bg-primary hover:bg-secondary md:w-auto">
              {sending ? "Sending…" : "Send Message"}
            </Button>
          </form>
        </div>
      </section>

      <section className="pb-20">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-6 flex items-end justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold">Find Us</p>
              <h2 className="mt-2 font-display text-3xl font-semibold text-primary md:text-4xl">Visit our campus</h2>
            </div>
            <a
              href={settings.map_direction_url}
              target="_blank"
              rel="noreferrer"
              className="hidden text-sm font-semibold text-secondary hover:text-primary md:inline"
            >
              Get directions →
            </a>
          </div>
          <LocationMap height={480} src={settings.map_embed_url || undefined} />
        </div>
      </section>
    </PageShell>
  );
}
